import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AppThemeProvider } from './theme/AppThemeProvider';
import { authService } from './services/authService';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import ProfilePage from './pages/ProfilePage';
import AnalysisPage from './pages/AnalysisPage';
import DataPage from './pages/DataPage';
import RunPredictorPage from './pages/RunPredictorPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import { CircularProgress, Box } from '@mui/material';

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    const checkAuth = () => {
      setIsAuthenticated(authService.isAuthenticated());
    };

    checkAuth();

    // Listen for storage changes (logout from other tabs)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'strava_access_token') {
        checkAuth();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  if (isAuthenticated === null) {
    return (
      <AppThemeProvider>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            backgroundColor: '#000000',
          }}
        >
          <CircularProgress sx={{ color: '#FC5200' }} />
        </Box>
      </AppThemeProvider>
    );
  }

  return (
    <AppThemeProvider>
      <Router>
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              isAuthenticated ? <Navigate to="/profile" replace /> : <LoginPage />
            }
          />
          <Route
            path="/auth/callback"
            element={
              isAuthenticated ? <Navigate to="/profile" replace /> : <AuthCallbackPage />
            }
          />

          {/* Public pages */}
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />

          {/* Protected routes */}
          <Route
            path="/*"
            element={
              isAuthenticated ? (
                <Layout>
                  <Routes>
                    <Route path="/profile" element={<ProfilePage />} />
                    <Route path="/analysis" element={<AnalysisPage />} />
                    <Route path="/data" element={<DataPage />} />
                    <Route path="/predictor" element={<RunPredictorPage />} />
                    <Route path="/" element={<Navigate to="/profile" replace />} />
                  </Routes>
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
      </Router>
    </AppThemeProvider>
  );
};

export default App;